import React from 'react';

import { Box, Flex, Image } from '@chakra-ui/react';
import { L3_PATHS } from 'Deer/constants/routes';
import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { setIsExpandedOneOnOneSidebar } from 'screens/oneOnOneRabbit/InstancePage/slice/OneOnOneInstancePageSlice';
import { getLegacyOneOnOneInstancePagePath, getOneOnOneInstancePagePath } from 'screens/oneOnOneRabbit/helpers';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { OneOnOneIconDualTone } from 'assets/assetsV1/iconComponents';
import NoOneOnOneImage from 'assets/assetsV1/illustrations/noOneOnOne.svg';

import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { SidebarCardTemplate } from '../../common/components/SidebarCardTemplate';
import { usePastOneOnOnes } from './hooks';

const COMPLETION_STATUS = {
  COMPLETED: {
    label: 'Completed',
    color: '#1A5724',
  },
  YET_TO_START: {
    label: 'Current',
    color: '#666666',
  },
  CANCELLED: {
    label: 'Canceled',
    color: '#C62C02',
  },
};

const OneOnOneSection = () => {
  const { masterId } = useOneOnOnePageParams();

  const { l1Module, teamType } = useRouteToNavLevels();

  const isMyData = l1Module === 'myData';

  const { data, isLoading } = usePastOneOnOnes({
    masterId,
  });

  const history = useHistory();
  const dispatch = useDispatch();

  const selectedCohort = useSelectedView();

  const redirectToOneOnOneInstance = ({ masterId, instanceId, legacyInstanceId }) => {
    if (legacyInstanceId) {
      if (checkNewNavEnabled()) {
        const routes = {
          myData: L3_PATHS['my-data'].oneOnOne.legacyPage,
          team: {
            directReports: L3_PATHS.team.directReports.oneOnOne.legacyPage,
            allReports: L3_PATHS.team.allReports.oneOnOne.legacyPage,
          },
        };

        const route = isMyData ? routes.myData : routes.team[teamType as 'directReports' | 'allReports'];
        const legacyRedirectRoute = generatePath(route, {
          instanceId: legacyInstanceId,
        });

        history.push(legacyRedirectRoute);
        return;
        // eslint-disable-next-line no-else-return
      } else {
        const legacyRedirectRoute = getLegacyOneOnOneInstancePagePath({
          legacyInstanceId,
        });
        history.push(legacyRedirectRoute);
        return;
      }
    }
    const redirectRoute = getOneOnOneInstancePagePath({
      masterId,
      instanceId,
      selectedCohort: selectedCohort?.id,
    });
    history.push(redirectRoute);
  };

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!data?.length) {
    return (
      <SidebarCardTemplate label="Past 1:1s" Icon={OneOnOneIconDualTone}>
        <Flex mt="24px" flexDir="column" alignItems="center" justifyContent="center">
          <Image width="172px" src={NoOneOnOneImage as unknown as string} />
          <Flex w="245px" textAlign="center" mt="22px" flexDir="column" p="12px">
            <TextBodyMedium bold>There is nothing to see here</TextBodyMedium>
            <TextBodyMedium color="text.g2">Looks like you don't have any completed or canceled 1:1s</TextBodyMedium>
          </Flex>
        </Flex>
      </SidebarCardTemplate>
    );
  }

  return (
    <SidebarCardTemplate label="Past 1:1s" Icon={OneOnOneIconDualTone}>
      {data?.map(({ id, startTime, startTimeTs, completionStatus, masterId, legacyInstanceId }) => (
        <Box
          as="button"
          sx={{ borderRadius: '8px', background: 'white', margin: '4px 0px', height: '56px' }}
          onClick={() => {
            redirectToOneOnOneInstance({ masterId, instanceId: id, legacyInstanceId });
            dispatch(setIsExpandedOneOnOneSidebar(false));
          }}
          key={id}
        >
          <Flex p="12px" alignItems="center">
            <TextBodyMedium bold>
              Date : {format(startTimeTs ? new Date(startTimeTs * 1000) : startTime, 'dd MMM, yyyy')}
            </TextBodyMedium>
            <TextBodyMedium ml="auto" color={COMPLETION_STATUS[completionStatus]?.color}>
              {COMPLETION_STATUS[completionStatus]?.label}
            </TextBodyMedium>
          </Flex>
        </Box>
      ))}
    </SidebarCardTemplate>
  );
};

export { OneOnOneSection };
